import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Paper, Typography, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, Grid, CircularProgress, IconButton, InputAdornment,
  ButtonGroup, Popper, Grow, ClickAwayListener, MenuList, ListItemIcon,
  ListItemText, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip, Tooltip,
  ToggleButtonGroup, ToggleButton, Collapse
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PublishIcon from "@mui/icons-material/Publish";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CropIcon from "@mui/icons-material/Crop";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CategoriesManager from "../CategoriesManager/CategoriesManager";
import RichTextEditor from "components/RichTextEditor";
import ImageUploader from "components/ImageUploader";
import FeaturedImageFocusPicker from "./components/FeaturedImageFocusPicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  getPostById, createPost, updatePost, getAllCategories, previewPost
} from "api/canblog/CANblogAPI";
import { ROUTE_CANBLOG, ROUTE_CANBLOG_EDITOR } from "constants/routerPath";
import useNotificationProvider from "hooks/notificationBar/useNotificationProvider";

function BlogPostEditor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postId = searchParams.get("id");
  const isEditMode = Boolean(postId);
  const { notifySuccess, notifyError, notifyWarning } = useNotificationProvider();

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    content: "",
    excerpt: "",
    featuredImageUrl: "",
    featuredImageFocus: "50,50", // "x,y" percent; drives hero/card crop
    authorName: "",
    authorEmployeeId: null,
    status: "draft",
    publishDate: null,
    metaTitle: "",
    metaDescription: "",
    categoryId: "",
    contentMode: "richtext" // "richtext" = TinyMCE | "html" = raw imported HTML
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [autoSlug, setAutoSlug] = useState(true); // Auto-generate slug from title
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [publishMenuOpen, setPublishMenuOpen] = useState(false);
  const publishAnchorRef = useRef(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(dayjs().add(1, "hour"));
  const savedFormDataRef = useRef(null);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);

  // Content-mode state
  const [modeSwitchDialogOpen, setModeSwitchDialogOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [focusPickerOpen, setFocusPickerOpen] = useState(false);

  // Fetch categories on mount and set initial snapshot for new posts
  useEffect(() => {
    fetchCategories();
    if (!isEditMode) {
      savedFormDataRef.current = { ...formData };
    }
  }, []);

  // Fetch post data if editing
  useEffect(() => {
    if (isEditMode) {
      fetchPost();
    }
  }, [postId]);

  const fetchCategories = async () => {
    try {
      const data = await getAllCategories();
      setCategories(data || []);
    } catch (err) {
      console.error("[BlogPostEditor] Failed to fetch categories:", err);
      notifyWarning("Could not load categories");
    }
  };

  const fetchPost = async () => {
    setLoading(true);
    try {
      const post = await getPostById(postId);
      if (post) {
        setFormData({
          title: post.Title || "",
          slug: post.Slug || "",
          content: post.Content || "",
          excerpt: post.Excerpt || "",
          featuredImageUrl: post.FeaturedImageUrl || "",
          featuredImageFocus: post.FeaturedImageFocus || "50,50",
          authorName: post.AuthorName || "",
          authorEmployeeId: post.AuthorEmployeeId || null,
          status: post.Status || "draft",
          publishDate: post.PublishDate ? dayjs(post.PublishDate) : null,
          metaTitle: post.MetaTitle || "",
          metaDescription: post.MetaDescription || "",
          categoryId: post.CategoryId || "",
          contentMode: post.ContentFormat || "richtext"
        });
        setAutoSlug(false); // Don't auto-generate slug for existing posts
        savedFormDataRef.current = {
          title: post.Title || "",
          slug: post.Slug || "",
          content: post.Content || "",
          excerpt: post.Excerpt || "",
          featuredImageUrl: post.FeaturedImageUrl || "",
          featuredImageFocus: post.FeaturedImageFocus || "50,50",
          authorName: post.AuthorName || "",
          authorEmployeeId: post.AuthorEmployeeId || null,
          status: post.Status || "draft",
          publishDate: post.PublishDate ? dayjs(post.PublishDate) : null,
          metaTitle: post.MetaTitle || "",
          metaDescription: post.MetaDescription || "",
          categoryId: post.CategoryId || "",
          contentMode: post.ContentFormat || "richtext"
        };
      }
    } catch (err) {
      console.error("[BlogPostEditor] Failed to load post:", err);
      notifyError("Failed to load post. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Check for unsaved changes (compare editable content fields only)
  const hasUnsavedChanges = () => {
    if (!savedFormDataRef.current) return false;
    const fields = ["title", "slug", "content", "excerpt", "featuredImageUrl", "featuredImageFocus", "authorName", "metaTitle", "metaDescription", "categoryId", "contentMode"];
    return fields.some(field => formData[field] !== savedFormDataRef.current[field]);
  };

  // Warn on browser back / tab close if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [formData]);

  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-"); // Remove duplicate hyphens
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: autoSlug ? generateSlug(title) : prev.slug
    }));
  };

  const handleSlugChange = (e) => {
    setAutoSlug(false);
    setFormData(prev => ({ ...prev, slug: e.target.value }));
  };

  const handleRegenerateSlug = () => {
    setFormData(prev => ({ ...prev, slug: generateSlug(prev.title) }));
    setAutoSlug(true);
  };

  const handleFieldChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditorChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
  };

  // Content-mode toggle. Switching surfaces is destructive in practice
  // (TinyMCE re-parses/normalizes HTML on mount), so warn first whenever
  // there is existing content to lose.
  const handleModeChange = (_e, newMode) => {
    if (!newMode || newMode === formData.contentMode) return;
    if (formData.content && formData.content.trim() !== "") {
      setPendingMode(newMode);
      setModeSwitchDialogOpen(true);
    } else {
      setFormData(prev => ({ ...prev, contentMode: newMode }));
    }
  };

  const confirmModeSwitch = () => {
    if (pendingMode) {
      setFormData(prev => ({ ...prev, contentMode: pendingMode }));
    }
    setPendingMode(null);
    setModeSwitchDialogOpen(false);
  };

  const handlePreview = () => {
    setSubmitAttempted(true);
    if (hasErrors) {
      notifyWarning("Fill in title, slug, and content before previewing.");
      return;
    }
    // Open the tab synchronously inside the click handler so the browser
    // does not treat it as a blocked popup; fill it once the render returns.
    const previewWindow = window.open("", "_blank");
    if (previewWindow) {
      previewWindow.document.write(
        "<!doctype html><title>Generating preview…</title>" +
        "<body style='font-family:sans-serif;padding:2rem;color:#475569'>Generating preview…</body>"
      );
    }

    setPreviewing(true);
    previewPost({
      title: formData.title,
      slug: formData.slug,
      content: formData.content,
      excerpt: formData.excerpt,
      featuredImageUrl: formData.featuredImageUrl,
      featuredImageFocus: formData.featuredImageFocus,
      authorName: formData.authorName,
      publishDate: formData.publishDate ? formData.publishDate.toISOString() : null,
      categoryId: formData.categoryId || null,
      contentFormat: formData.contentMode
    })
      .then((html) => {
        if (!previewWindow || previewWindow.closed) {
          notifyWarning("Preview was blocked. Allow popups for this site and try again.");
          return;
        }
        previewWindow.document.open();
        previewWindow.document.write(html);
        previewWindow.document.close();
      })
      .catch((err) => {
        console.error("[BlogPostEditor] Preview failed:", err);
        if (previewWindow && !previewWindow.closed) previewWindow.close();
        notifyError(err.message || "Failed to generate preview. Please try again.");
      })
      .finally(() => setPreviewing(false));
  };

  const isPublished = formData.status === "published";
  const isScheduledForFuture = isPublished && formData.publishDate && formData.publishDate.isAfter(dayjs());

  // Inline validation
  const errors = {
    title: !formData.title.trim() ? "Title is required" : "",
    slug: !formData.slug.trim() ? "Slug is required" : "",
    content: !formData.content.trim() ? "Content is required" : "",
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const showFieldError = (field) => submitAttempted && Boolean(errors[field]);
  const isActionDisabled = saving || (submitAttempted && hasErrors);

  const getButtonTooltip = () => {
    if (saving) return "Saving in progress...";
    if (submitAttempted && hasErrors) return "Please fill in all required fields";
    return "";
  };

  const handleSave = async (options = {}) => {
    const { status = formData.status, publishDate = formData.publishDate, successMessage } = options;

    setSubmitAttempted(true);
    if (hasErrors) return;

    setSaving(true);

    try {
      const postData = {
        title: formData.title,
        slug: formData.slug,
        content: formData.content,
        excerpt: formData.excerpt,
        featuredImageUrl: formData.featuredImageUrl,
        featuredImageFocus: formData.featuredImageFocus,
        authorName: formData.authorName,
        authorEmployeeId: formData.authorEmployeeId,
        status,
        publishDate: publishDate ? publishDate.toISOString() : null,
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        categoryId: formData.categoryId || null,
        contentFormat: formData.contentMode
      };

      const defaultMessage = isEditMode ? "Post updated successfully" : "Post created successfully";

      if (isEditMode) {
        postData.id = parseInt(postId, 10);
        await updatePost(postData);
        setFormData(prev => ({ ...prev, status, publishDate }));
        notifySuccess(successMessage || defaultMessage);
      } else {
        const result = await createPost(postData);
        setFormData(prev => ({ ...prev, status, publishDate }));
        notifySuccess(successMessage || defaultMessage);
        // Navigate to edit mode with new post ID
        if (result?.BlogPostId) {
          navigate(`${ROUTE_CANBLOG_EDITOR}?id=${result.BlogPostId}`, { replace: true });
        }
      }
      // Update saved snapshot so unsaved changes check is reset
      savedFormDataRef.current = { ...formData, status, publishDate };
    } catch (err) {
      console.error("[BlogPostEditor] Failed to save post:", err);
      notifyError(err.message || "Failed to save post. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = () => {
    setPublishMenuOpen(false);
    handleSave({ status: "published", publishDate: dayjs(), successMessage: "Post published successfully" });
  };

  const handleScheduleConfirm = () => {
    const date = scheduleDate.format("MMM D, YYYY [at] HH:mm");
    setScheduleDialogOpen(false);
    handleSave({ status: "published", publishDate: scheduleDate, successMessage: `Post scheduled for ${date}` });
  };

  const handleSaveDraft = () => {
    handleSave({ status: "draft", successMessage: "Draft saved" });
  };

  const handleUnpublish = () => {
    handleSave({ status: "draft", publishDate: null, successMessage: "Post unpublished and reverted to draft" });
  };

  const handleBack = () => {
    if (hasUnsavedChanges()) {
      setUnsavedDialogOpen(true);
    } else {
      navigate(ROUTE_CANBLOG);
    }
  };

  const handleConfirmLeave = () => {
    setUnsavedDialogOpen(false);
    navigate(ROUTE_CANBLOG);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            {isEditMode ? "Edit Post" : "New Post"}
          </Typography>
        </Box>

        {/* Form */}
        <Grid container spacing={3}>
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={handleTitleChange}
              required
              error={showFieldError("title")}
              helperText={showFieldError("title") ? errors.title : ""}
            />
          </Grid>

          {/* Slug */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Slug"
              value={formData.slug}
              onChange={handleSlugChange}
              required
              error={showFieldError("slug")}
              helperText={showFieldError("slug") ? errors.slug : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleRegenerateSlug} title="Regenerate from title">
                      <AutorenewIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* Category */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.categoryId}
                  onChange={handleFieldChange("categoryId")}
                  label="Category"
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.CategoryId} value={cat.CategoryId}>
                      {cat.CategoryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                onClick={() => setCategoriesModalOpen(true)}
                title="Manage Categories"
                sx={{ alignSelf: "center" }}
              >
                <SettingsIcon />
              </IconButton>
            </Box>
          </Grid>

          {/* Excerpt */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Excerpt"
              value={formData.excerpt}
              onChange={handleFieldChange("excerpt")}
              multiline
              rows={2}
              helperText="Short description for previews (max 500 characters)"
              inputProps={{ maxLength: 500 }}
            />
          </Grid>

          {/* Featured Image */}
          <Grid item xs={12}>
            <ImageUploader
              value={formData.featuredImageUrl}
              onChange={(url) => setFormData(prev => ({
                ...prev,
                featuredImageUrl: url,
                // A new image invalidates the old focal point — reset to centre.
                featuredImageFocus: url === prev.featuredImageUrl ? prev.featuredImageFocus : "50,50",
              }))}
              imageVisibility="public"
              label="Featured Image"
              helperText="This image will be displayed at the top of your blog post"
            />
            {formData.featuredImageUrl ? (
              <Box
                sx={{
                  mt: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  role="button"
                  tabIndex={0}
                  aria-expanded={focusPickerOpen}
                  onClick={() => setFocusPickerOpen((o) => !o)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setFocusPickerOpen((o) => !o);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    cursor: "pointer",
                    userSelect: "none",
                    bgcolor: focusPickerOpen ? "action.hover" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <CropIcon fontSize="small" color="action" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                      Adjust Focal Point
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Control how the featured image is cropped on different screen sizes
                    </Typography>
                  </Box>
                  <ExpandMoreIcon
                    fontSize="small"
                    color="action"
                    sx={{
                      transition: "transform 0.2s ease",
                      transform: focusPickerOpen ? "rotate(180deg)" : "none",
                    }}
                  />
                </Box>
                <Collapse in={focusPickerOpen} unmountOnExit>
                  <Box sx={{ px: 2, pb: 2 }}>
                    <FeaturedImageFocusPicker
                      imageUrl={formData.featuredImageUrl}
                      value={formData.featuredImageFocus}
                      onChange={(focus) => setFormData(prev => ({ ...prev, featuredImageFocus: focus }))}
                      title={formData.title}
                      authorName={formData.authorName}
                      categoryName={
                        categories.find((c) => c.CategoryId === formData.categoryId)?.CategoryName || ""
                      }
                    />
                  </Box>
                </Collapse>
              </Box>
            ) : null}
          </Grid>

          {/* Content Editor */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" color={showFieldError("content") ? "error" : "text.primary"}>
                Content *
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ToggleButtonGroup
                  value={formData.contentMode}
                  exclusive
                  onChange={handleModeChange}
                  size="small"
                >
                  <ToggleButton value="richtext">Rich Text Editor</ToggleButton>
                  <ToggleButton value="html">HTML</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {formData.contentMode === "richtext" ? (
              <RichTextEditor
                value={formData.content}
                onChange={handleEditorChange}
                imageVisibility="public"
                height={400}
                placeholder="Write your blog post content here..."
              />
            ) : (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={16}
                  value={formData.content}
                  onChange={(e) => handleEditorChange(e.target.value)}
                  placeholder="Paste or edit raw HTML here. This content bypasses the rich text editor."
                  InputProps={{ sx: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: "0.85rem" } }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Raw HTML mode — the rich text editor will not modify this content. Scripts and unsupported tags are removed when the post is published.
                </Typography>
              </Box>
            )}
            {showFieldError("content") && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {errors.content}
              </Typography>
            )}
          </Grid>

          {/* Author Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Author Name"
              value={formData.authorName}
              onChange={handleFieldChange("authorName")}
            />
          </Grid>

          {/* Publish Status */}
          {isPublished && (
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {isScheduledForFuture ? (
                  <>
                    <Chip label="Scheduled" color="warning" size="small" icon={<ScheduleIcon />} />
                    <Typography variant="body2" color="text.secondary">
                      {formData.publishDate.format("MMM D, YYYY [at] HH:mm")}
                    </Typography>
                    <Button size="small" onClick={() => setUnpublishDialogOpen(true)}>
                      Cancel schedule
                    </Button>
                  </>
                ) : (
                  <>
                    <Chip label="Published" color="success" size="small" icon={<PublishIcon />} />
                    <Typography variant="body2" color="text.secondary">
                      {formData.publishDate ? formData.publishDate.format("MMM D, YYYY [at] HH:mm") : ""}
                    </Typography>
                    <Button size="small" onClick={() => setUnpublishDialogOpen(true)}>
                      Unpublish
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          )}

          {/* SEO Fields */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>SEO Settings</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Meta Title"
              value={formData.metaTitle}
              onChange={handleFieldChange("metaTitle")}
              inputProps={{ maxLength: 255 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Meta Description"
              value={formData.metaDescription}
              onChange={handleFieldChange("metaDescription")}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", alignItems: "center" }}>
              {submitAttempted && hasErrors && (
                <Typography variant="caption" color="error">
                  Please fill in all required fields
                </Typography>
              )}
              <Button variant="outlined" onClick={handleBack}>
                Return
              </Button>
              <Button
                variant="outlined"
                startIcon={previewing ? <CircularProgress size={16} /> : <VisibilityIcon />}
                onClick={handlePreview}
                disabled={previewing}
              >
                Preview
              </Button>
              <Tooltip title={getButtonTooltip()} disableHoverListener={!isActionDisabled}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleSaveDraft}
                    disabled={isActionDisabled}
                  >
                    {saving ? <CircularProgress size={20} /> : "Save Draft"}
                  </Button>
                </span>
              </Tooltip>
              {isPublished ? (
                <Tooltip title={getButtonTooltip()} disableHoverListener={!isActionDisabled}>
                  <span>
                    <ButtonGroup variant="contained" ref={publishAnchorRef}>
                      <Button
                        onClick={() => handleSave()}
                        disabled={isActionDisabled}
                      >
                        {saving ? <CircularProgress size={20} /> : "Update"}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setPublishMenuOpen((prev) => !prev)}
                        disabled={isActionDisabled}
                      >
                        <ArrowDropDownIcon />
                      </Button>
                    </ButtonGroup>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={getButtonTooltip()} disableHoverListener={!isActionDisabled}>
                  <span>
                    <ButtonGroup variant="contained" ref={publishAnchorRef}>
                      <Button
                        onClick={handlePublishNow}
                        disabled={isActionDisabled}
                      >
                        {saving ? <CircularProgress size={20} /> : "Publish Now"}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setPublishMenuOpen((prev) => !prev)}
                        disabled={isActionDisabled}
                      >
                        <ArrowDropDownIcon />
                      </Button>
                    </ButtonGroup>
                  </span>
                </Tooltip>
              )}
              <Popper
                open={publishMenuOpen}
                anchorEl={publishAnchorRef.current}
                placement="top-end"
                transition
                disablePortal
                sx={{ zIndex: 1 }}
              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps}>
                    <Paper elevation={4}>
                      <ClickAwayListener onClickAway={() => setPublishMenuOpen(false)}>
                        <MenuList>
                          <MenuItem onClick={handlePublishNow}>
                            <ListItemIcon><PublishIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Publish Now</ListItemText>
                          </MenuItem>
                          <MenuItem onClick={() => {
                            setPublishMenuOpen(false);
                            setScheduleDate(dayjs().add(1, "hour"));
                            setScheduleDialogOpen(true);
                          }}>
                            <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Schedule...</ListItemText>
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Unsaved Changes Dialog */}
      <Dialog open={unsavedDialogOpen} onClose={() => setUnsavedDialogOpen(false)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setUnsavedDialogOpen(false)}>Stay</Button>
          <Button variant="contained" color="error" onClick={handleConfirmLeave}>Leave</Button>
        </DialogActions>
      </Dialog>

      {/* Unpublish Confirmation Dialog */}
      <Dialog open={unpublishDialogOpen} onClose={() => setUnpublishDialogOpen(false)}>
        <DialogTitle>{isScheduledForFuture ? "Cancel Schedule" : "Unpublish Post"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isScheduledForFuture
              ? "This will cancel the scheduled publication and revert the post to draft. Continue?"
              : "This will remove the post from the public website and revert it to draft. Continue?"
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setUnpublishDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { setUnpublishDialogOpen(false); handleUnpublish(); }}>
            {isScheduledForFuture ? "Cancel Schedule" : "Unpublish"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)}>
        <DialogTitle>Schedule Publication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Choose when this post should go live.
          </Typography>
          <DateTimePicker
            label="Publish Date & Time"
            value={scheduleDate}
            onChange={setScheduleDate}
            ampm={false}
            minDateTime={dayjs()}
            renderInput={(params) => (
              <TextField {...params} fullWidth />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleScheduleConfirm}>
            Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mode Switch Warning Dialog */}
      <Dialog open={modeSwitchDialogOpen} onClose={() => { setModeSwitchDialogOpen(false); setPendingMode(null); }}>
        <DialogTitle>Switch editor mode?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingMode === "richtext"
              ? "Switching to the rich text editor will load this content into TinyMCE, which may reformat or strip HTML it does not support. This can permanently change your imported markup."
              : "Switching to HTML mode will hand the current content to the raw HTML editor. Rich text formatting may not round-trip cleanly back."}
            {" "}Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => { setModeSwitchDialogOpen(false); setPendingMode(null); }}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={confirmModeSwitch}>
            Switch mode
          </Button>
        </DialogActions>
      </Dialog>

      {/* Categories Manager Modal */}
      <CategoriesManager
        open={categoriesModalOpen}
        onClose={() => {
          setCategoriesModalOpen(false);
          fetchCategories(); // Refresh categories after modal closes
        }}
      />

    </LocalizationProvider>
  );
}

export default BlogPostEditor;
